import { useCallback, useEffect } from "react";
import { mapValues } from "lodash-es";
import { Location } from "history";
import { match, RouteComponentProps, RouteProps as ReactRouterRouteProps } from "react-router";
// eslint-disable-next-line no-restricted-imports
import { Route as ReactRouterRoute } from "react-router-dom";
import { GlobalFiltersType } from "../../app/GlobalDashboardFilters/globalFiltersTypes";
import { currentRouteMatch } from "../../routes/routeMatch";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";

type RouteProps = Pick<ReactRouterRouteProps, "exact" | "path"> & {
    component?: React.ComponentType<Record<string, unknown>>;
    render?: (props: RouteComponentProps & { key?: string }) => React.ReactNode;
    children?: React.ReactNode;
    globalFilters?: GlobalFiltersType;
    suppressLocationKeyRemount?: boolean;
    suppressGlobalFiltersRemount?: boolean;
    [key: string]: unknown;
};

/**
 * Syncs the current route match to the reactive store after commit.
 * Must run in a useEffect (not during render) to avoid triggering
 * React's concurrent-mode tearing detection when Zustand subscribers
 * exist higher in the tree.
 */
function RouteMatchSync({ match }: { match: match }) {
    useEffect(() => {
        currentRouteMatch.setState(match);
    }, [match]);
    return null;
}

const Route = ({
    component,
    render,
    children,
    globalFilters,
    suppressLocationKeyRemount = false,
    suppressGlobalFiltersRemount = false,
    exact,
    path,
    ...extraProps
}: RouteProps) => {
    if (component) {
        const Component = component;
        render = (p: RouteComponentProps) => (
            <Component {...p} {...extraProps}>
                {children}
            </Component>
        );
    } else if (!render && children) {
        render = () => <>{children}</>;
    }
    // TODO Remove this workaround when upgrading to react-router@6
    //  react-route<6 have a bug that causes "/user/:id" route with "/user/123?a=b&c=d#section" to
    //  have match.params={ id: "123?a=b&c=d#section" }, instead of match.params=: { id: "123" }
    //  this is caused by a bug in react-route<6's dependency called path-to-regexp@0.1.7
    // TODO "/route/:id" with URL "/route/hello%20a%2Fb" translates to
    //  params: { id: "hello a%2Fb" } instead of { id: "hello a/b" },
    //  Can upgrading react-router(-dom) fix this? If so, remove this workaround
    const fixParamsParsingBug = useCallback((oldProps: RouteComponentProps) => {
        if (!oldProps.match.params) return oldProps;
        const fixedMatch = { ...oldProps.match };
        if (fixedMatch.params) {
            fixedMatch.params = mapValues(fixedMatch.params, value =>
                decodeURIComponent(/^[^/#?]+/.exec(value)?.[0] ?? value),
            );
        }
        return { ...oldProps, match: fixedMatch };
    }, []);

    return (
        <ReactRouterRoute
            exact={exact}
            path={path}
            render={routeInjectedProps => (
                <ErrorBoundary key={path as string} section={`route:${path}`}>
                    <RouteMatchSync match={routeInjectedProps.match} />
                    {render?.({
                        ...fixParamsParsingBug(routeInjectedProps),
                        key: routeKey(routeInjectedProps.location, globalFilters, {
                            suppressLocationKeyRemount,
                            suppressGlobalFiltersRemount,
                        }),
                    })}
                </ErrorBoundary>
            )}
        />
    );
};

function routeKey(
    location: Location,
    globalFilters: GlobalFiltersType | undefined,
    { suppressLocationKeyRemount = false, suppressGlobalFiltersRemount = false },
): string | undefined {
    const keyParts = [
        !suppressLocationKeyRemount && location.key,
        !suppressGlobalFiltersRemount && JSON.stringify(globalFilters),
    ]
        .filter(Boolean)
        .join(",");
    return keyParts || undefined;
}

export default Route;
